import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Slider,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowUpOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { FundingStatus } from '../components/FundingStatus';
import LocCard from '../components/LocCard.tsx';
import { useAuth } from '../app/AuthContext';
import { useTranslation } from 'react-i18next';
import api, { DashboardData, OwnershipSnapshot, ProfitAllocation, User as UserData } from '../api';
import { userReopenRequestsEndpoints } from '../api/adminReopenRequestsEndpoints';
import { creditLineEndpoints, CreditLineEligibility } from '../api/creditLineEndpoints';
import { formatCurrency } from '../utils/formatters';
import dayjs from 'dayjs';
import './Dashboard.css';

const { Text } = Typography;

type ChartRange = '5D' | '1M' | '3M' | 'YTD' | '1Y' | 'ALL';

interface PrincipalHistoryPoint {
  isoDate: string;
  label: string;
  timestamp: number;
  valueCents: number;
}

interface ChartCoordinate {
  x: number;
  y: number;
}

interface PrincipalChartPoint extends ChartCoordinate {
  isoDate: string;
  valueCents: number;
}

interface PrincipalChartModel {
  areaPath: string;
  changeCents: number;
  changePercent: number;
  gridLines: number[];
  hasData: boolean;
  linePath: string;
  pointCount: number;
  points: PrincipalChartPoint[];
  trend: 'positive' | 'negative' | 'flat';
  lastPoint: ChartCoordinate;
}

const DASHBOARD_RANGES: Array<{ key: ChartRange; label: string }> = [
  { key: '5D', label: '5D' },
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: 'YTD', label: 'YTD' },
  { key: '1Y', label: '1Y' },
  { key: 'ALL', label: 'ALL' },
];

function getInitials(name?: string | null) {
  if (!name) return 'U';

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

function buildPrincipalHistory(
  snapshots: OwnershipSnapshot[],
  userId: string,
  currentBasePrincipalCents: number,
): PrincipalHistoryPoint[] {
  const history = snapshots
    .map((snapshot) => {
      const entry = snapshot.entries.find((candidate) => candidate.user_id === userId);

      if (!entry) return null;

      return {
        isoDate: snapshot.as_of,
        label: dayjs(snapshot.as_of).format('MMM D'),
        timestamp: dayjs(snapshot.as_of).valueOf(),
        valueCents: entry.base_principal_cents,
      };
    })
    .filter((point): point is PrincipalHistoryPoint => point !== null)
    .sort((left, right) => left.timestamp - right.timestamp);

  if (history.length === 0) {
    return [
      {
        isoDate: new Date().toISOString(),
        label: dayjs().format('MMM D'),
        timestamp: Date.now(),
        valueCents: currentBasePrincipalCents,
      },
    ];
  }

  const lastPoint = history[history.length - 1];
  const shouldAppendCurrentPoint =
    lastPoint.valueCents !== currentBasePrincipalCents ||
    dayjs().diff(dayjs(lastPoint.isoDate), 'hour') >= 12;

  if (shouldAppendCurrentPoint) {
    history.push({
      isoDate: new Date().toISOString(),
      label: dayjs().format('MMM D'),
      timestamp: Date.now(),
      valueCents: currentBasePrincipalCents,
    });
  }

  return history;
}

function getRangeStartTimestamp(range: ChartRange) {
  const now = dayjs();

  switch (range) {
    case '5D':
      return now.subtract(5, 'day').valueOf();
    case '1M':
      return now.subtract(1, 'month').valueOf();
    case '3M':
      return now.subtract(3, 'month').valueOf();
    case 'YTD':
      return now.startOf('year').valueOf();
    case '1Y':
      return now.subtract(1, 'year').valueOf();
    case 'ALL':
    default:
      return Number.NEGATIVE_INFINITY;
  }
}

function filterPrincipalHistory(points: PrincipalHistoryPoint[], range: ChartRange) {
  if (points.length <= 2 || range === 'ALL') {
    return points;
  }

  const rangeStart = getRangeStartTimestamp(range);
  const visiblePoints = points.filter((point) => point.timestamp >= rangeStart);
  const previousPoint = [...points].reverse().find((point) => point.timestamp < rangeStart);

  let filteredPoints = previousPoint ? [previousPoint, ...visiblePoints] : visiblePoints;

  if (filteredPoints.length === 0) {
    filteredPoints = points.slice(Math.max(0, points.length - 2));
  }

  if (filteredPoints.length === 1 && points.length > 1) {
    filteredPoints = [points[points.length - 2], filteredPoints[0]];
  }

  return filteredPoints;
}

function buildPrincipalChart(points: PrincipalHistoryPoint[], currentBasePrincipalCents: number): PrincipalChartModel {
  const fallbackPoint: PrincipalHistoryPoint = {
    isoDate: new Date().toISOString(),
    label: dayjs().format('MMM D'),
    timestamp: Date.now(),
    valueCents: currentBasePrincipalCents,
  };
  const sourcePoints = points.length > 0 ? points : [fallbackPoint];
  const normalizedPoints =
    sourcePoints.length > 1
      ? sourcePoints
      : [
          sourcePoints[0],
          {
            ...sourcePoints[0],
            timestamp: sourcePoints[0].timestamp + 1,
          },
        ];

  const width = 1000;
  const height = 260;
  const paddingTop = 18;
  const paddingRight = 18;
  const paddingBottom = 28;
  const paddingLeft = 18;
  const chartBottom = height - paddingBottom;
  const timestamps = normalizedPoints.map((point) => point.timestamp);
  const values = normalizedPoints.map((point) => point.valueCents);
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueSpan = maxValue - minValue || Math.max(Math.round(maxValue * 0.05), 10_000);
  const paddedMin = Math.max(0, minValue - valueSpan * 0.35);
  const paddedMax = maxValue + valueSpan * 0.15;

  const getScaledX = (timestamp: number, index: number, totalPoints: number) => {
    if (maxTimestamp !== minTimestamp) {
      return paddingLeft + ((timestamp - minTimestamp) / (maxTimestamp - minTimestamp)) * (width - paddingLeft - paddingRight);
    }

    if (totalPoints <= 1) {
      return width / 2;
    }

    return paddingLeft + (index / (totalPoints - 1)) * (width - paddingLeft - paddingRight);
  };

  const getScaledY = (valueCents: number) => {
    const yProgress = paddedMax === paddedMin ? 0.5 : (valueCents - paddedMin) / (paddedMax - paddedMin);
    return chartBottom - yProgress * (chartBottom - paddingTop);
  };

  const coordinates = normalizedPoints.map((point, index) => ({
    x: getScaledX(point.timestamp, index, normalizedPoints.length),
    y: getScaledY(point.valueCents),
  }));

  const sourcePointCoordinates = sourcePoints.map((point, index) => ({
    x: getScaledX(point.timestamp, index, sourcePoints.length),
    y: getScaledY(point.valueCents),
    isoDate: point.isoDate,
    valueCents: point.valueCents,
  }));

  let linePath = `M ${coordinates[0].x} ${coordinates[0].y}`;

  for (let index = 1; index < coordinates.length; index += 1) {
    linePath += ` H ${coordinates[index].x} V ${coordinates[index].y}`;
  }

  const lastPoint = coordinates[coordinates.length - 1];
  const areaPath = `${linePath} L ${lastPoint.x} ${chartBottom} L ${coordinates[0].x} ${chartBottom} Z`;
  const changeCents = normalizedPoints[normalizedPoints.length - 1].valueCents - normalizedPoints[0].valueCents;
  const firstValue = normalizedPoints[0].valueCents;
  const changePercent = firstValue > 0 ? (changeCents / firstValue) * 100 : 0;

  return {
    areaPath,
    changeCents,
    changePercent,
    gridLines: [0.22, 0.5, 0.78].map((progress) => paddingTop + progress * (chartBottom - paddingTop)),
    hasData: sourcePoints.length > 0,
    linePath,
    pointCount: sourcePoints.length,
    points: sourcePointCoordinates,
    trend: changeCents > 0 ? 'positive' : changeCents < 0 ? 'negative' : 'flat',
    lastPoint,
  };
}

export function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reinvestPercent, setReinvestPercent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawForm] = Form.useForm();
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);
  const [recentAllocations, setRecentAllocations] = useState<ProfitAllocation[]>([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const [reopenStatus, setReopenStatus] = useState<any>(null);
  const [requestingReopen, setRequestingReopen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<ChartRange>('5D');
  const [principalHistory, setPrincipalHistory] = useState<PrincipalHistoryPoint[]>([]);
  const [hoveredChartPoint, setHoveredChartPoint] = useState<PrincipalChartPoint | null>(null);

  const [oneTimeModalOpen, setOneTimeModalOpen] = useState(false);
  const [oneTimeAmount, setOneTimeAmount] = useState(0);
  const [savingOneTime, setSavingOneTime] = useState(false);

  const [hasPlaidLinked, setHasPlaidLinked] = useState(false);
  const [locEligibility, setLocEligibility] = useState<CreditLineEligibility | null>(null);

  useEffect(() => {
    fetchData();
    fetchPlaidStatus();
  }, []);

  useEffect(() => {
    if (userData?.participation_status === 'exited') {
      fetchReopenStatus();
    }
  }, [userData?.participation_status]);

  const fetchData = async () => {
    try {
      setAllocationsLoading(true);

      const [userResponse, dashboardResponse, allocationsResponse, ownershipHistoryResponse] = await Promise.all([
        api.users.me(),
        api.dashboard.getDashboard(),
        api.allocations.getMyAllocations({ per: 5 }).catch((error) => {
          console.error('Failed to fetch allocations:', error);
          return null;
        }),
        api.ownership.getHistory(200).catch((error) => {
          console.error('Failed to fetch ownership history:', error);
          return null;
        }),
      ]);

      setUserData(userResponse.user);
      setDashboardData(dashboardResponse);
      setReinvestPercent(dashboardResponse.reinvest_percent);
      setRecentAllocations(allocationsResponse?.allocations ?? []);

      if (ownershipHistoryResponse) {
        setPrincipalHistory(
          buildPrincipalHistory(
            ownershipHistoryResponse.snapshots,
            userResponse.user.id,
            userResponse.user.base_principal_cents,
          ),
        );
      } else {
        setPrincipalHistory(
          buildPrincipalHistory([], userResponse.user.id, userResponse.user.base_principal_cents),
        );
      }

      if (userResponse.user.eligible_for_earnings) {
        try {
          const eligibilityResponse = await creditLineEndpoints.getEligibility();
          setLocEligibility(eligibilityResponse.eligibility);
        } catch (error) {
          console.error('Failed to fetch LOC eligibility:', error);
          setLocEligibility(null);
        }
      } else {
        setLocEligibility(null);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setAllocationsLoading(false);
      setLoading(false);
    }
  };

  const fetchPlaidStatus = async () => {
    try {
      const response = await api.payments.getProfile();
      setHasPlaidLinked(response.has_linked_account);
    } catch (error) {
      console.error('Failed to fetch Plaid status:', error);
    }
  };

  const fetchReopenStatus = async () => {
    try {
      const status = await userReopenRequestsEndpoints.getStatus();
      setReopenStatus(status);
    } catch (error) {
      console.error('Failed to fetch reopen status:', error);
    }
  };

  const handleRequestReopen = () => {
    Modal.confirm({
      title: 'Request to Reopen Account',
      icon: <ReloadOutlined />,
      content: (
        <Space direction="vertical">
          <Text>
            Reopening your account requires admin approval and a new funding requirement. Once approved,
            you&apos;ll need to fund your account before you can participate in profit allocations again.
          </Text>
          <Text type="secondary">
            Your historical data will be preserved, but you&apos;ll start a new participation period.
          </Text>
        </Space>
      ),
      okText: 'Submit Request',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRequestingReopen(true);
          await userReopenRequestsEndpoints.requestReopen();
          message.success('Reopen request submitted successfully. An admin will review your request.');
          fetchReopenStatus();
        } catch (error: any) {
          message.error(error.message || 'Failed to submit reopen request');
        } finally {
          setRequestingReopen(false);
        }
      },
    });
  };

  const handleSaveReinvestPercent = async () => {
    if (reinvestPercent < 0 || reinvestPercent > 100) {
      message.error(t('profitSettings.invalidPercentage'));
      return;
    }

    try {
      setSaving(true);
      await api.users.updateReinvestPercent(reinvestPercent);
      await fetchData();
      message.success(t('profitSettings.saveSuccess'));
    } catch (error: any) {
      console.error('Failed to update reinvest percent:', error);
      message.error(error.message || t('profitSettings.saveError'));

      if (dashboardData) {
        setReinvestPercent(dashboardData.reinvest_percent);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReinvestPercentChange = (value: number | null) => {
    if (value === null) return;

    setReinvestPercent(Math.max(0, Math.min(100, value)));
  };

  const calculateTimeUntilSettlement = (nextSettlement: string) => {
    const now = new Date();
    const settlement = new Date(nextSettlement);
    const diff = settlement.getTime() - now.getTime();

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  const calculateLivePreview = () => {
    if (!dashboardData) {
      return { reinvestCents: 0, withdrawCents: 0 };
    }

    const profitCents = dashboardData.profit_week_cents;
    const reinvestCents = Math.round((profitCents * reinvestPercent) / 100);

    return {
      reinvestCents,
      withdrawCents: profitCents - reinvestCents,
    };
  };

  const handleQuickPercentage = (percent: number) => {
    const withdrawableCents = userData?.withdrawable_cents || 0;
    const amount = Math.round((withdrawableCents * percent) / 100);
    withdrawForm.setFieldsValue({ amount_cents: amount });
  };

  const handleWithdrawSubmit = async (values: { amount_cents: number; user_note?: string }) => {
    try {
      setSubmittingWithdraw(true);
      await api.withdrawals.create(values);
      message.success(t('withdrawModal.submitSuccess'));
      setWithdrawModalOpen(false);
      withdrawForm.resetFields();
      await fetchData();
    } catch (error: any) {
      console.error('Failed to submit withdrawal:', error);
      message.error(error.message || t('withdrawModal.submitError'));
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  const openWithdrawModal = () => {
    withdrawForm.resetFields();
    setWithdrawModalOpen(true);
  };

  const openOneTimeModal = () => {
    const currentAmount = (userData?.one_time_withdrawable_amount_cents || 0) / 100;
    setOneTimeAmount(currentAmount);
    setOneTimeModalOpen(true);
  };

  const handleOneTimeAmountChange = (value: number | null) => {
    if (value === null) return;

    const profitDollars = (userData?.profit_week_cents || 0) / 100;
    setOneTimeAmount(Math.max(0, Math.min(profitDollars, value)));
  };

  const handleSaveOneTime = async () => {
    try {
      setSavingOneTime(true);
      const amountCents = Math.round(oneTimeAmount * 100);
      await api.users.setOneTimeWithdrawable(amountCents);
      message.success(t('oneTimeWithdraw.saveSuccess'));
      setOneTimeModalOpen(false);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to set one-time withdrawable:', error);
      message.error(error.message || t('oneTimeWithdraw.saveError'));
    } finally {
      setSavingOneTime(false);
    }
  };

  const handleClearOneTime = async () => {
    try {
      setSavingOneTime(true);
      await api.users.clearOneTimeWithdrawable();
      message.success(t('oneTimeWithdraw.clearSuccess'));
      setOneTimeModalOpen(false);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to clear one-time withdrawable:', error);
      message.error(error.message || t('oneTimeWithdraw.saveError'));
    } finally {
      setSavingOneTime(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading-screen">
        <Spin size="large" />
      </div>
    );
  }

  const livePreview = calculateLivePreview();
  const isEligible = dashboardData?.eligible_for_earnings ?? userData?.eligible_for_earnings ?? false;
  const visibleHistory = filterPrincipalHistory(principalHistory, selectedRange);
  const chartModel = buildPrincipalChart(visibleHistory, userData?.base_principal_cents || 0);
  const chartChangePrefix = chartModel.changeCents > 0 ? '+' : chartModel.changeCents < 0 ? '-' : '';
  const withdrawableShare = userData
    ? Math.min(
        100,
        Math.round(
          ((userData.withdrawable_cents || 0) /
            Math.max((userData.withdrawable_cents || 0) + (userData.base_principal_cents || 0), 1)) *
            100,
        ),
      )
    : 0;
  const settlementCountdown = dashboardData ? calculateTimeUntilSettlement(dashboardData.next_settlement) : null;
  const showLocCard = Boolean(userData?.eligible_for_earnings && locEligibility?.eligible);
  const hasUnsavedReinvestChanges = reinvestPercent !== dashboardData?.reinvest_percent;

  const updateHoveredChartPoint = (clientX: number, target: SVGSVGElement) => {
    if (chartModel.points.length === 0) {
      setHoveredChartPoint(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(rect.width, 1)));
    const chartX = relativeX * 1000;

    let nearestPoint = chartModel.points[0];
    let nearestDistance = Math.abs(nearestPoint.x - chartX);

    for (let index = 1; index < chartModel.points.length; index += 1) {
      const candidate = chartModel.points[index];
      const distance = Math.abs(candidate.x - chartX);

      if (distance < nearestDistance) {
        nearestPoint = candidate;
        nearestDistance = distance;
      }
    }

    setHoveredChartPoint(nearestPoint);
  };

  return (
    <div className="dashboard-redesign">
      {isAdmin && userData && !userData.base_principal_initialized_at && (
        <Alert
          title={t('adminAlert.title')}
          description={
            <Space direction="vertical" size="small">
              <span>{t('adminAlert.description')}</span>
              <Button type="primary" icon={<WarningOutlined />} onClick={() => navigate('/admin/bootstrap')}>
                {t('adminAlert.button')}
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          closable={false}
        />
      )}

      {!isAdmin &&
        userData &&
        userData.participation_status &&
        (userData.participation_status === 'pending_close' || userData.participation_status === 'exited') && (
          <Alert
            type={userData.participation_status === 'pending_close' ? 'warning' : 'info'}
            showIcon
            icon={
              userData.participation_status === 'pending_close' ? (
                <ClockCircleOutlined />
              ) : (
                <InfoCircleOutlined />
              )
            }
            title={
              <strong>
                {userData.participation_status === 'pending_close'
                  ? t('closureStatus.pendingTitle', 'Account Closure Pending')
                  : t('closureStatus.exitedTitle', 'Account Closed')}
              </strong>
            }
            description={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>
                  {userData.participation_status === 'pending_close'
                    ? t(
                        'closureStatus.pendingMessage',
                        'Your account closure request is being processed. You will receive one more profit allocation in the next weekly settlement, after which your account will be closed and a final withdrawal will be created.',
                      )
                    : t(
                        'closureStatus.exitedMessage',
                        'Your account has been closed and you are no longer participating in the pool. No new profits will be allocated to you.',
                      )}
                </Text>
                {userData.participation_status === 'exited' && (
                  <>
                    <Text type="secondary">
                      {t(
                        'closureStatus.historyAvailable',
                        'You can still view your historical contributions, settlements, and withdrawals below.',
                      )}
                    </Text>
                    {reopenStatus && reopenStatus.reopen_request?.status === 'pending_admin_review' ? (
                      <Alert
                        type="info"
                        title="Reopen Request Pending"
                        description="Your request to reopen your account is being reviewed by an admin. You will be notified once it has been approved or rejected."
                        showIcon
                      />
                    ) : (
                      reopenStatus?.can_request_reopen && (
                        <Button
                          type="primary"
                          icon={<ReloadOutlined />}
                          onClick={handleRequestReopen}
                          loading={requestingReopen}
                        >
                          Request to Reopen Account
                        </Button>
                      )
                    )}
                  </>
                )}
              </Space>
            }
          />
        )}

      {!isAdmin && userData && !userData.eligible_for_earnings && <FundingStatus onStatusChange={fetchData} />}

      <section className="dashboard-hero-card">
        <div className="dashboard-hero-top">
          <div className="dashboard-profile-block">
            <div className="dashboard-profile-avatar">{getInitials(userData?.full_name || user?.full_name)}</div>
            <div className="dashboard-profile-copy">
              <h1>{userData?.full_name || user?.full_name || 'User'}</h1>
              <p>{t('description')}</p>
            </div>
          </div>

          {settlementCountdown && (
            <button type="button" className="dashboard-settlement-pill">
              <ClockCircleOutlined />
              <span className="dashboard-settlement-pill__label">Next settlement:</span>
              <strong>{settlementCountdown}</strong>
              <DownOutlined />
            </button>
          )}
        </div>

        <div className="dashboard-hero-main">
          <div className="dashboard-hero-value-block">
            <div className="dashboard-hero-value">{formatCurrency(userData?.base_principal_cents || 0)}</div>
            <div className="dashboard-hero-label">{t('stats.basePrincipal')}</div>
            <div className={`dashboard-hero-delta dashboard-hero-delta--${chartModel.trend}`}>
              <ArrowUpOutlined rotate={chartModel.trend === 'negative' ? 180 : 0} />
              <span>{`${chartChangePrefix}${formatCurrency(Math.abs(chartModel.changeCents))}`}</span>
              <span>{`(${chartModel.changePercent >= 0 ? '+' : ''}${chartModel.changePercent.toFixed(2)}%)`}</span>
              <span>{selectedRange}</span>
            </div>
          </div>

          <div className="dashboard-chart-shell">
            {chartModel.hasData ? (
              <>
                <svg
                  className="dashboard-principal-chart"
                  viewBox="0 0 1000 260"
                  preserveAspectRatio="none"
                  aria-label="Base principal history"
                  onMouseMove={(event) => updateHoveredChartPoint(event.clientX, event.currentTarget)}
                  onMouseLeave={() => setHoveredChartPoint(null)}
                  onTouchStart={(event) => {
                    const touch = event.touches[0];
                    if (touch) {
                      updateHoveredChartPoint(touch.clientX, event.currentTarget);
                    }
                  }}
                  onTouchMove={(event) => {
                    const touch = event.touches[0];
                    if (touch) {
                      updateHoveredChartPoint(touch.clientX, event.currentTarget);
                    }
                  }}
                  onTouchEnd={() => setHoveredChartPoint(null)}
                >

                {chartModel.gridLines.map((lineY) => (
                  <line
                    key={lineY}
                    x1="18"
                    x2="982"
                    y1={lineY}
                    y2={lineY}
                    className="dashboard-principal-chart__grid"
                  />
                ))}

                <path d={chartModel.areaPath} className="dashboard-principal-chart__area" />
                <path d={chartModel.linePath} className="dashboard-principal-chart__line" />
                <circle
                  cx={(hoveredChartPoint || chartModel.lastPoint).x}
                  cy={(hoveredChartPoint || chartModel.lastPoint).y}
                  r="5"
                  className="dashboard-principal-chart__dot"
                />
              </svg>

                {hoveredChartPoint && (
                  <div
                    className="dashboard-chart-bubble"
                    style={{
                      left: `${(hoveredChartPoint.x / 1000) * 100}%`,
                      top: `${Math.max(((hoveredChartPoint.y / 260) * 180) - 54, 6)}px`,
                    }}
                  >
                    <span>{dayjs(hoveredChartPoint.isoDate).format('MMM D, YYYY')}</span>
                    <strong>{formatCurrency(hoveredChartPoint.valueCents)}</strong>
                  </div>
                )}
              </>
            ) : (
              <div className="dashboard-chart-empty">No history available yet</div>
            )}
          </div>
        </div>

        <div className="dashboard-range-tabs" role="tablist" aria-label="Base principal time ranges">
          {DASHBOARD_RANGES.map((range) => (
            <button
              key={range.key}
              type="button"
              className={`dashboard-range-tab${selectedRange === range.key ? ' is-active' : ''}`}
              onClick={() => {
                setSelectedRange(range.key);
                setHoveredChartPoint(null);
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
      </section>

      {showLocCard && <LocCard />}

      <div className="dashboard-summary-grid">
        <section className="dashboard-shell-card dashboard-withdrawable-card">
          <div className="dashboard-card-header-row">
            <div className="dashboard-card-title-group">
              <span className="dashboard-card-icon dashboard-card-icon--gold">
                <WalletOutlined />
              </span>
              <h2>{t('stats.withdrawable')}</h2>
            </div>

            <Button
              type="primary"
              className="dashboard-primary-button"
              onClick={openWithdrawModal}
              disabled={!userData || userData.withdrawable_cents === 0}
            >
              {t('stats.requestButton')}
            </Button>
          </div>

          <div className="dashboard-balance-value-row">
            <div className="dashboard-balance-value">{formatCurrency(userData?.withdrawable_cents || 0)}</div>
            <div className="dashboard-balance-meta">{dashboardData?.ownership_percent || '0.00%'}</div>
          </div>

          <div className="dashboard-balance-caption">Available to withdrawal</div>

          <div className="dashboard-balance-progress" aria-hidden="true">
            <span style={{ width: `${Math.max(withdrawableShare, userData?.withdrawable_cents ? 6 : 0)}%` }} />
          </div>
        </section>

        <section className="dashboard-shell-card dashboard-profit-settings-card">
          <div className="dashboard-card-header-row dashboard-card-header-row--top-aligned">
            <div className="dashboard-card-title-group dashboard-card-title-group--stacked">
              <span className="dashboard-card-icon dashboard-card-icon--amber">
                <WalletOutlined />
              </span>
              <div>
                <h2>{t('profitSettings.title')}</h2>
                {!isEligible && <Tag color="orange">{t('profitSettings.notEligibleTag')}</Tag>}
              </div>
            </div>

            <div className="dashboard-settings-actions">
              {userData && userData.participation_status === 'active' && userData.profit_week_cents > 0 && (
                <Button onClick={openOneTimeModal}>{t('oneTimeWithdraw.button')}</Button>
              )}
              <Button
                type="primary"
                className="dashboard-primary-button"
                onClick={handleSaveReinvestPercent}
                loading={saving}
                disabled={!isEligible || saving || !hasUnsavedReinvestChanges}
              >
                {t('profitSettings.saveButton')}
              </Button>
            </div>
          </div>

          <div className="dashboard-slider-label-row">
            <span>{t('profitSettings.reinvestmentPercentage')}</span>
            <strong>{reinvestPercent}%</strong>
          </div>

          <div className="dashboard-slider-row">
            <Slider
              min={0}
              max={100}
              value={reinvestPercent}
              onChange={handleReinvestPercentChange}
              disabled={!isEligible || saving}
              tooltip={{ formatter: (value) => `${value}%` }}
              className="dashboard-slider"
            />
            <InputNumber
              min={0}
              max={100}
              value={reinvestPercent}
              onChange={handleReinvestPercentChange}
              disabled={!isEligible || saving}
              formatter={(value) => `${value}%`}
              parser={(value) => Number(value?.replace('%', '')) || 0}
              className="dashboard-slider-input"
            />
          </div>

          {hasUnsavedReinvestChanges && <div className="dashboard-unsaved-indicator">{t('profitSettings.unsavedChanges')}</div>}

          <div className="dashboard-preview-grid dashboard-preview-grid--flat">
            <div className="dashboard-preview-row dashboard-preview-row--green">
              <div className="dashboard-preview-label">{t('profitSettings.basePrincipal')}</div>
              <div className="dashboard-preview-value">{formatCurrency(livePreview.reinvestCents)}</div>
              <div className="dashboard-preview-note">{t('profitSettings.addedOnSettlement')}</div>
            </div>

            <div className="dashboard-preview-row dashboard-preview-row--gold">
              <div className="dashboard-preview-label">{t('profitSettings.withdrawable')}</div>
              <div className="dashboard-preview-value">{formatCurrency(livePreview.withdrawCents)}</div>
              <div className="dashboard-preview-note">{t('profitSettings.addedOnSettlement')}</div>
            </div>
          </div>

          {userData && (userData.one_time_withdrawable_amount_cents || 0) > 0 && (
            <div className="dashboard-one-time-note">
              <span>{t('oneTimeWithdraw.pendingLabel')}</span>
              <strong>{formatCurrency(userData.one_time_withdrawable_amount_cents || 0)}</strong>
              <span>{t('oneTimeWithdraw.remainingNote')}</span>
            </div>
          )}

          {!isEligible && userData && userData.contribution_shortfall_cents > 0 && (
            <Alert
              title={t('profitSettings.notEligibleAlert.title')}
              description={t('profitSettings.notEligibleAlert.description', {
                amount: formatCurrency(userData.contribution_shortfall_cents),
              })}
              type="warning"
              showIcon
            />
          )}
        </section>
      </div>

      <Card
        className="dashboard-recent-card"
        title={t('recentAllocations.title')}
        variant="borderless"
        extra={
          recentAllocations.length > 0 && (
            <Button type="link" onClick={() => navigate('/history')}>
              {t('recentAllocations.viewAll')}
            </Button>
          )
        }
      >
        {allocationsLoading ? (
          <div className="dashboard-recent-loading">
            <Spin />
          </div>
        ) : recentAllocations.length > 0 ? (
          <table className="dashboard-recent-table">
            <thead>
              <tr>
                <th>{t('recentAllocations.date')}</th>
                <th>{t('recentAllocations.gross')}</th>
                <th>{t('recentAllocations.companyCut')}</th>
                <th>{t('recentAllocations.net')}</th>
              </tr>
            </thead>
            <tbody>
              {recentAllocations.map((allocation, index) => (
                <tr
                  key={allocation.id}
                  className={index < recentAllocations.length - 1 ? 'dashboard-recent-row' : 'dashboard-recent-row dashboard-recent-row--last'}
                >
                  <td>{dayjs(allocation.profit_entry_date).format('MMM D, YYYY')}</td>
                  <td>{formatCurrency(allocation.gross_share_cents)}</td>
                  <td>{formatCurrency(allocation.company_cut_cents)}</td>
                  <td className="dashboard-recent-net">{formatCurrency(allocation.net_share_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dashboard-recent-empty">
            <Text type="secondary">{t('recentAllocations.noAllocations')}</Text>
          </div>
        )}
      </Card>

      <Modal
        title={t('withdrawModal.title')}
        open={withdrawModalOpen}
        onCancel={() => setWithdrawModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Alert
          message={
            <Space>
              <BankOutlined />
              {hasPlaidLinked ? (
                <>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Plaid Connected</span>
                </>
              ) : (
                <>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>No Plaid Account Connected</span>
                </>
              )}
            </Space>
          }
          description={
            !hasPlaidLinked && (
              <span>
                Large withdrawals require Plaid. <Link to="/settings">Connect your bank account</Link> in settings.
              </span>
            )
          }
          type={hasPlaidLinked ? 'success' : 'warning'}
          showIcon={false}
          style={{ marginBottom: 16 }}
        />

        <Alert
          title={t('withdrawModal.processingTime.title')}
          description={t('withdrawModal.processingTime.description')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={withdrawForm}
          layout="vertical"
          onFinish={handleWithdrawSubmit}
          initialValues={{ amount_cents: 0 }}
        >
          <Form.Item
            label={
              <Space>
                <span>{t('withdrawModal.amount')}</span>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('withdrawModal.available', { amount: formatCurrency(userData?.withdrawable_cents || 0) })}
                </Text>
              </Space>
            }
            name="amount_cents"
            rules={[
              { required: true, message: t('withdrawModal.amountRequired') },
              {
                validator: (_, value) => {
                  if (value <= 0) {
                    return Promise.reject(new Error(t('withdrawModal.amountGreaterThanZero')));
                  }

                  if (value > (userData?.withdrawable_cents || 0)) {
                    return Promise.reject(new Error(t('withdrawModal.amountExceedsBalance')));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={userData?.withdrawable_cents || 0}
              formatter={(value) => {
                const dollars = (Number(value) || 0) / 100;
                return `$ ${dollars.toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }}
              parser={(value) => {
                const cleaned = value?.replace(/\$\s?|(,*)/g, '') || '0';
                return Math.round(Number(cleaned) * 100);
              }}
              placeholder={t('withdrawModal.placeholder')}
            />
          </Form.Item>

          <Space style={{ marginBottom: 16, width: '100%' }} wrap>
            <Button size="small" onClick={() => handleQuickPercentage(25)}>
              25%
            </Button>
            <Button size="small" onClick={() => handleQuickPercentage(50)}>
              50%
            </Button>
            <Button size="small" onClick={() => handleQuickPercentage(75)}>
              75%
            </Button>
            <Button size="small" onClick={() => handleQuickPercentage(100)}>
              100%
            </Button>
          </Space>

          <Form.Item label={t('withdrawModal.noteLabel')} name="user_note">
            <Input.TextArea
              rows={3}
              placeholder={t('withdrawModal.notePlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setWithdrawModalOpen(false)}>{t('withdrawModal.cancelButton')}</Button>
              <Button type="primary" htmlType="submit" loading={submittingWithdraw} icon={<WalletOutlined />}>
                {t('withdrawModal.submitButton')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('oneTimeWithdraw.modalTitle')}
        open={oneTimeModalOpen}
        onCancel={() => setOneTimeModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">{t('oneTimeWithdraw.currentProfit')}:</Text>
            <br />
            <Text strong style={{ fontSize: 18 }}>
              {formatCurrency(userData?.profit_week_cents || 0)}
            </Text>
          </div>

          <div>
            <Text strong>{t('oneTimeWithdraw.amountLabel')}:</Text>
            <br />
            <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
              <Col flex="auto">
                <Slider
                  min={0}
                  max={(userData?.profit_week_cents || 0) / 100}
                  step={0.01}
                  value={oneTimeAmount}
                  onChange={handleOneTimeAmountChange}
                  tooltip={{
                    formatter: (value) => `$${(value || 0).toFixed(2)}`,
                  }}
                />
              </Col>
              <Col>
                <InputNumber
                  min={0}
                  max={(userData?.profit_week_cents || 0) / 100}
                  step={0.01}
                  value={oneTimeAmount}
                  onChange={handleOneTimeAmountChange}
                  formatter={(value) => `$ ${(Number(value) || 0).toFixed(2)}`}
                  parser={(value) => parseFloat(value?.replace(/\$\s?|(,*)/g, '') || '0')}
                  style={{ width: 120 }}
                />
              </Col>
            </Row>
          </div>

          <Alert message={t('oneTimeWithdraw.helpText')} type="info" showIcon />

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {(userData?.one_time_withdrawable_amount_cents || 0) > 0 && (
              <Button onClick={handleClearOneTime} loading={savingOneTime}>
                {t('oneTimeWithdraw.clearButton')}
              </Button>
            )}
            <Button onClick={() => setOneTimeModalOpen(false)}>{t('oneTimeWithdraw.cancelButton')}</Button>
            <Button type="primary" onClick={handleSaveOneTime} loading={savingOneTime}>
              {t('oneTimeWithdraw.saveButton')}
            </Button>
          </Space>
        </Space>
      </Modal>
    </div>
  );
}

export default Dashboard;